import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import { useSession } from '@ais-ui/websockets';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useMemo } from 'react';
import { getBasePath } from './getAISBasePath';
import {
  Configuration,
  MessageApi,
  MessageSchedulePostRequest,
  MessageSendPostOperationRequest,
  ReceivedMessagesResponse,
  type SchedulesResponse,
  type SentMessagesResponse,
} from '@ais-ui/api-spec/config';
import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

const useMessagesApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();

  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}/aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const messagesApi = useMemo(() => {
    return new MessageApi(configuration);
  }, [configuration]);
  return {
    messagesApi: apiClientWrapper(messagesApi) as MessageApi,
    configuration,
  };
};

export const useMessagesReceivedList = <T = ReceivedMessagesResponse>(
  begin: Date | undefined,
  end: Date | undefined,
  options?: {
    select: (data: ReceivedMessagesResponse) => T;
  }
) => {
  const { messagesApi } = useMessagesApi();
  return useQuery(
    ['receivedMessages', begin, end],
    async () => {
      const result = await messagesApi.messageReceiveListGet({
        dc: '1',
        aisweb: 'true',
        begin: begin as Date,
        end: end as Date,
      });

      if (result) {
        convertFieldsToDates(result, ['records.time', 'records.formattedTime']);
      }

      return result;
    },
    { select: options?.select }
  );
};

export const useMessagesSentList = <T = SentMessagesResponse>(
  begin: Date | undefined,
  end: Date | undefined,
  options?: {
    select: (data: SentMessagesResponse) => T;
  }
) => {
  const { messagesApi } = useMessagesApi();
  return useQuery(
    ['sentMessages', begin, end],
    async () => {
      const result = await messagesApi.messageSendListGet({
        dc: '1',
        aisweb: 'true',
        begin: begin as Date,
        end: end as Date,
      });

      if (result) {
        convertFieldsToDates(result, ['records.time', 'records.formattedTime']);
      }

      return result;
    },
    { select: options?.select }
  );
};
export const useMessagesScheduledList = <T = SchedulesResponse>(options?: {
  select: (data: SchedulesResponse) => T;
}) => {
  const { messagesApi } = useMessagesApi();
  return useQuery(
    ['scheduledMessages'],
    async () => {
      const result = await messagesApi.messageScheduleGet({
        dc: '1',
      });

      if (result) {
        convertFieldsToDates(result, [
          'records.schedule.end',
          'records.schedule.start',
        ]);
      }

      return result;
    },
    { select: options?.select }
  );
};

export const useSendMessage = (): UseMutationResult<
  void,
  Error,
  MessageSendPostOperationRequest,
  unknown
> => {
  const { messagesApi } = useMessagesApi();
  const queryClient = useQueryClient();

  return useMutation(
    (message: MessageSendPostOperationRequest) => {
      return messagesApi.messageSendPost(message);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['sentMessages']),
    }
  );
};

export const useScheduleMessage = (): UseMutationResult<
  void,
  Error,
  MessageSchedulePostRequest,
  unknown
> => {
  const { messagesApi } = useMessagesApi();
  const queryClient = useQueryClient();

  return useMutation(
    (scheduledMessage: MessageSchedulePostRequest) => {
      return messagesApi.messageSchedulePost(scheduledMessage);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['scheduledMessages']),
    }
  );
};

export const useEditScheduleMessage = (): UseMutationResult<
  void,
  Error,
  MessageSchedulePostRequest,
  unknown
> => {
  const { messagesApi } = useMessagesApi();
  const queryClient = useQueryClient();

  return useMutation(
    (scheduledMessage: MessageSchedulePostRequest) => {
      return messagesApi.messageSchedulePut(scheduledMessage);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['scheduledMessages']),
    }
  );
};

export const useDeleteMessageSchedule = (): UseMutationResult<
  void,
  Error,
  string,
  unknown
> => {
  const { messagesApi } = useMessagesApi();
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => {
      return messagesApi.messageScheduleUidDelete({ uid: id, dc: '1' });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['scheduledMessages']),
    }
  );
};
